import {
  EventAudienceTypeDescriptionDictionary,
  buildAudienceSelectOptionsForUser,
  channelsForUserGroup,
  getCurrentAudienceForEvent,
} from '@/src/components/events/eventsCommon/EventChannelTypes';
import { NullableNumber } from '@/src/types/common';
import { EventPrivacyType } from '@/src/types/EventPrivacyType';
import { IEventTemplate } from '@/src/types/EventTemplate';
import { EventFields } from '@/src/utils/event.validation.utils';
import {
  Badge,
  Checkbox,
  Container,
  ExpandableSection,
  FormField,
  Grid,
  Header,
  Input,
  KeyValuePairs,
  Select,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import * as awsui from '@amzn/awsui-design-tokens/polaris.js';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EventAudienceType } from '../../../constants/shared/event-audience-type';
import { EditCampaignActions, useEditCampaign } from '../../../store/edit-campaign.context';
import { EditEventActions, useEditEvent } from '../../../store/edit-event.context';
import { useUser } from '../../../store/user.context';
import '../../../styles/tags.scss';
import { Campaign } from '../../../types/Campaign';
import { Event } from '../../../types/Event';
import { isEventInSameTimezoneAsBrowser } from '../../../utils/event-time.utils';
import { i18nKeys } from '../../../utils/i18n.utils';
import { audienceTypeDictionary } from '../../events/event-list-config';
import EventTimes from '../../events/eventDetailsSections/Summary/EventTimes';
import { DateTimeKeys, TimezoneFormat } from '../CommonModel';
import { DateTime } from '../DateTime';
import { EventTags } from '../EventTags';
import { KeyValue } from '../KeyValue';

const addIf = <T,>(add: boolean, value: T) => (add ? [value] : []);

interface TargetSummaryDetailsProps {
  target: Event | Campaign | undefined;
  eventTemplate?: IEventTemplate | undefined;
  handleCancelOrDeleteEvent?: () => void;
  eventTitleValidation?: {
    isValidSection: (setErrors?: boolean | undefined) => boolean;
    isValidField: (field: EventFields, setError?: boolean | undefined) => boolean;
  };
  titleError?: string;
  channelError?: string;
}

const TargetSummaryDetails: React.FC<TargetSummaryDetailsProps> = ({
  target,
  eventTemplate,
  eventTitleValidation,
  titleError,
  channelError,
}) => {
  const { t } = useTranslation();
  const { editMode, newEventMode, editedEvent, isGuestUserAndEditModeEnabled, handleUpdateEditEvent } = useEditEvent();
  const { campaignEditMode, newCampaignMode, editedCampaign, handleUpdateEditCampaign } = useEditCampaign();
  const { user } = useUser();

  const isEvent = target instanceof Event;

  const canEdit = useMemo(() => {
    return target instanceof Event ? editMode || newEventMode : campaignEditMode || newCampaignMode;
  }, [editMode, newEventMode, campaignEditMode, newCampaignMode]);

  const privacyType = (target as Event)?.eventPrivacyType as keyof typeof EventPrivacyType;

  // TODO: Fix design token import errors and replace javascript implementation below
  const SecondaryText = {
    color: awsui.colorTextFormSecondary,
  };

  const updateAudienceType = (newAudienceType: OptionDefinition) => {
    if (newAudienceType) {
      handleUpdateEditEvent(EditEventActions.AUDIENCE, newAudienceType.value);
    }
  };

  const channelsForGroup: Record<string, boolean> = useMemo(() => channelsForUserGroup(user), [user]);
  const audienceDictionary = useMemo(() => EventAudienceTypeDescriptionDictionary(t), [t]);

  const audienceType = (target as Event).audienceType || undefined;
  let selectedOption: OptionDefinition | undefined;

  const selectionOptions = useMemo(() => {
    if (!user) return [];
    if (!canEdit) return [];

    return buildAudienceSelectOptionsForUser(user, t);
  }, [user, canEdit, audienceType, t]);

  if (audienceType) {
    selectedOption = getCurrentAudienceForEvent(audienceType, t);
  }

  useEffect(() => {
    if (selectedOption || !selectionOptions.length) {
      return;
    }

    // Don't allow classroom events to have channel changed except by admin.
    if (audienceType === EventAudienceType.AWS_CLASSROOMS_BUILDER_LABS && !user?.isSuperAdmin) {
      return;
    }

    const defaultGroupChannelKey = Object.keys(channelsForGroup).find((key) => channelsForGroup[key]);

    const option =
      (defaultGroupChannelKey && selectionOptions.find((item) => item.value === defaultGroupChannelKey)) ||
      selectionOptions[0];

    updateAudienceType(option);
  }, [selectionOptions, channelsForGroup, audienceType, user]);

  const updateDisableCodeWhisperer = (value: boolean) => {
    handleUpdateEditEvent(EditEventActions.CODE_WHISPERER_DISABLED, value);
  };

  const handleTitleUpdate = (value: string) => {
    if ((value && typeof value !== 'string') || !value.trim()) {
      if (isEvent) {
        handleUpdateEditEvent(EditEventActions.TITLE, '');
      } else {
        handleUpdateEditCampaign(EditCampaignActions.TITLE, '');
      }
      return;
    }
    if (isEvent) {
      handleUpdateEditEvent(EditEventActions.TITLE, value);
    } else {
      handleUpdateEditCampaign(EditCampaignActions.TITLE, value);
    }
  };

  const showCampaignSlug = (): boolean => {
    if (target instanceof Campaign) {
      if (target.cancelled) {
        return false;
      }
      if (target.denied) {
        return false;
      }

      return target.approved;
    }
    return false;
  };

  const generateAutoUnlockString = (autoUnlockMinutes: NullableNumber) => {
    if (autoUnlockMinutes === null) {
      return `${t(i18nKeys.newEvent.labels.disabled)}`;
    }
    if (autoUnlockMinutes === 0) {
      return `${t(i18nKeys.newEvent.labels.enabled)} - ${t(
        i18nKeys.events.eventDetails.messages.autoUnlockChallengesInfoEventStartTime
      )}`;
    }
    return `${t(i18nKeys.newEvent.labels.enabled)} - ${autoUnlockMinutes} ${t(
      i18nKeys.events.eventDetails.messages.autoUnlockChallengesInfoMinutesAfterEventStartTime
    )}`;
  };

  const renderContent = () => {
    if (!canEdit) {
      if (user?.hasBasicAccess) {
        if (isEvent) {
          return (
            <KeyValuePairs
              columns={3}
              items={[
                {
                  label: t(i18nKeys.events.fields.eventTitle.title),
                  value: target?.title,
                },
                {
                  label: t(i18nKeys.events.fields.startTime.title),
                  value: (
                    <>
                      <DateTime
                        startOrEnd={DateTimeKeys.START}
                        event={target as Event}
                        timezoneFormat={TimezoneFormat.UTC}
                        displayInlineDateTime
                      />
                      {!isEventInSameTimezoneAsBrowser(target as Event) && (
                        <div>
                          ({t(i18nKeys.events.eventDetails.labels.yourLocalTime)}:{' '}
                          <DateTime
                            startOrEnd={DateTimeKeys.START}
                            event={target as Event}
                            timezoneFormat={TimezoneFormat.LOCAL}
                            displayInlineDateTime
                          />
                          )
                        </div>
                      )}
                    </>
                  ),
                },
                {
                  label: t(i18nKeys.events.fields.endTime.title),
                  value: (
                    <>
                      <DateTime
                        startOrEnd={DateTimeKeys.END}
                        event={target as Event}
                        timezoneFormat={TimezoneFormat.UTC}
                        displayInlineDateTime
                      />
                      {!isEventInSameTimezoneAsBrowser(target as Event) && (
                        <div>
                          ({t(i18nKeys.events.eventDetails.labels.yourLocalTime)} :{' '}
                          <DateTime
                            startOrEnd={DateTimeKeys.END}
                            event={target as Event}
                            timezoneFormat={TimezoneFormat.LOCAL}
                            displayInlineDateTime
                          />
                          )
                        </div>
                      )}
                    </>
                  ),
                },
                {
                  label: t(i18nKeys.events.fields.audience.title),
                  value: audienceTypeDictionary[(target as Event)?.audienceType ?? ''],
                },
                ...addIf((target as Event).catalogId === null, {
                  label: t(i18nKeys.challenges.challengeDetails.titles.minExpectedParticipants),
                  value: (target as Event).minExpectedParticipants,
                }),
                {
                  label: t(i18nKeys.events.fields.numberOfParticipants.title),
                  value: (target as Event).maxExpectedParticipants,
                },
                {
                  label: t(i18nKeys.events.fields.eventPrivacyType.label),
                  value:
                    t(i18nKeys.events.eventDetails.labels.evenPrivacyType[privacyType]) ||
                    t(i18nKeys.events.eventDetails.labels.evenPrivacyType.PRIVATE_CODE),
                },
                ...addIf(target?.getCreator()?.trim() !== '', {
                  label: t(i18nKeys.events.eventDetails.labels.createdBy),
                  value: (target as Event)?.getCreator(),
                }),
                {
                  label: t(i18nKeys.events.eventDetails.labels.challengeAutoUnlock),
                  value: generateAutoUnlockString(target.autoUnlockChallengesOffsetMinutes),
                },
              ]}
            />
          );
        } else {
          return (
            <KeyValuePairs
              columns={3}
              items={[
                {
                  label: t(i18nKeys.campaigns.labels.campaignDetails.campaignTitle),
                  value: target?.title,
                },
                ...(showCampaignSlug()
                  ? [
                      {
                        label: t(i18nKeys.events.fields.slug.title),
                        value: target?.slug,
                      },
                    ]
                  : []),
              ]}
            />
          );
        }
      }

      return (
        <div>
          <div className="section-first-row">
            <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
              <div style={SecondaryText}>
                {isEvent
                  ? t(i18nKeys.events.fields.eventTitle.title)
                  : t(i18nKeys.campaigns.labels.campaignDetails.campaignTitle)}
              </div>
              <div>{target?.title}</div>
            </Grid>
          </div>
          {((isEvent && target?.showEventId) || (target instanceof Campaign && showCampaignSlug())) && (
            <div className="grey-section-divider-top">
              <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
                <div style={SecondaryText}>{t(i18nKeys.events.fields.slug.title)}</div>
                <div>{isEvent ? target?.id : target.slug}</div>
              </Grid>
            </div>
          )}
          {isEvent && (
            <div className="grey-section-divider-top">
              <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
                <div style={SecondaryText}>{t(i18nKeys.events.fields.audience.title)}</div>
                <div>{audienceDictionary[target?.audienceType as string]}</div>
              </Grid>
            </div>
          )}
          <div className="grey-section-divider-top">
            <Grid gridDefinition={[{ colspan: 3 }, { colspan: 9 }]}>
              <div style={SecondaryText}>{t(i18nKeys.events.fields.tags.title)}</div>
              <div className="tag-group">
                {target?.tags?.map((tag, key) => {
                  return (
                    <Badge color="grey" key={key}>
                      {tag}
                    </Badge>
                  );
                })}
              </div>
            </Grid>
          </div>
        </div>
      );
    }

    if (!user) {
      return null;
    }

    return (
      <SpaceBetween direction="vertical" size="m">
        <KeyValue
          label={
            <b>
              {isEvent
                ? t(i18nKeys.events.fields.eventTitle.title)
                : t(i18nKeys.campaigns.labels.campaignDetails.campaignTitle)}
            </b>
          }
          required
        >
          <FormField
            errorText={titleError}
            i18nStrings={{ errorIconAriaLabel: t(i18nKeys.general.error) }}
            constraintText={`${
              isEvent
                ? t(i18nKeys.events.fields.eventTitle.title)
                : t(i18nKeys.campaigns.labels.campaignDetails.campaignTitle)
            } ${t(i18nKeys.generic.lengthLimit4to250)}. ${t(i18nKeys.general.avoidPIIWarning)}`}
          >
            <Input
              value={target?.title || ''}
              placeholder={
                isEvent
                  ? t(i18nKeys.events.eventDetails.labels.exampleJamTitle)
                  : t(i18nKeys.campaigns.labels.campaignDetails.exampleCampaignTitle)
              }
              readOnly={!target?.canEditAttribute('title', user)}
              onChange={({ detail }) => handleTitleUpdate(detail.value)}
              onBlur={() => eventTitleValidation && eventTitleValidation.isValidField(EventFields.EVENT_TITLE)}
              data-testid="eventTitleInput"
            />{' '}
          </FormField>
        </KeyValue>
        {editMode && <EventTimes target={target as Event} eventTemplate={eventTemplate} />}
        {isEvent &&
          !user.isOnlyBasicUser &&
          selectionOptions.length >= 1 &&
          !(audienceType === EventAudienceType.AWS_CLASSROOMS_BUILDER_LABS && !user.isSuperAdmin) && (
            <KeyValue label={<b>{t(i18nKeys.events.fields.audience.title)}</b>} required>
              <div style={SecondaryText}>{t(i18nKeys.events.eventDetails.messages.whoIsThisEventIntendedFor)}</div>
              <FormField i18nStrings={{ errorIconAriaLabel: t(i18nKeys.general.error) }} errorText={channelError}>
                <Select
                  disabled={!!isGuestUserAndEditModeEnabled}
                  options={selectionOptions}
                  selectedOption={selectedOption || null}
                  placeholder={t(i18nKeys.events.eventDetails.labels.placeHolder.selectChannel)}
                  onChange={({ detail }) => updateAudienceType(detail.selectedOption)}
                  onBlur={() => eventTitleValidation && eventTitleValidation.isValidField(EventFields.EVENT_CHANNEL)}
                  data-testid="eventChannelSelect"
                />
                {selectedOption &&
                  selectedOption.value &&
                  (() => {
                    const warningKey = `events.eventDetails.labels.channelWarnings.${selectedOption.value}`;
                    const warningText = t(warningKey as any);
                    // Only show warning if translation exists (doesn't return the key itself)
                    return warningText && warningText !== warningKey ? (
                      <div
                        style={{
                          marginTop: '8px',
                          padding: '8px',
                          backgroundColor: awsui.colorBackgroundDropdownItemHover,
                          borderLeft: `3px solid ${awsui.colorBorderStatusWarning}`,
                          borderRadius: '4px',
                        }}
                      >
                        <span style={{ fontWeight: 'bold' }}>{t(i18nKeys.general.warning)}: </span>
                        {warningText}
                      </div>
                    ) : null;
                  })()}
              </FormField>
            </KeyValue>
          )}
        {!user.hasBasicAccess && (editedEvent || newEventMode || editedCampaign || newCampaignMode) && (
          <KeyValue label={<b>{t(i18nKeys.events.eventDetails.headers.tags)}</b>}>
            <EventTags />
          </KeyValue>
        )}
        {isEvent && !user.hasBasicAccess && (
          <KeyValue label={<b>{t(i18nKeys.events.fields.disableCodeWhisperer.sectionTitle)}</b>}>
            <SpaceBetween direction="horizontal" size="xxxs">
              <Checkbox
                checked={target.codeWhispererDisabled ?? false}
                onChange={({ detail }) => updateDisableCodeWhisperer(detail.checked)}
                disabled={user?.hasBasicAccess}
                data-testid="amazonQCheckbox"
              />
              <div style={{ marginLeft: '1rem' }}>
                <div>{t(i18nKeys.events.fields.disableCodeWhisperer.title)}</div>
                <div style={SecondaryText}>{t(i18nKeys.events.fields.disableCodeWhisperer.description)}</div>
              </div>
            </SpaceBetween>
          </KeyValue>
        )}
      </SpaceBetween>
    );
  };

  return (
    <React.Fragment>
      {(isEvent ? !newEventMode : !newCampaignMode) && (
        <ExpandableSection
          defaultExpanded
          variant="container"
          data-testid="summary-overview-expandable"
          header={
            <Header variant="h2">
              {isEvent
                ? t(i18nKeys.events.eventDetails.headers.eventDetails)
                : t(i18nKeys.campaigns.headers.campaignDetails.campaignDetails)}
            </Header>
          }
        >
          {renderContent()}
        </ExpandableSection>
      )}
      {(isEvent ? newEventMode : newCampaignMode) && (
        <Container
          header={
            <Header variant="h2">
              {isEvent
                ? t(i18nKeys.events.eventDetails.headers.eventDetails)
                : t(i18nKeys.campaigns.headers.campaignDetails.campaignDetails)}
            </Header>
          }
        >
          {renderContent()}
        </Container>
      )}
    </React.Fragment>
  );
};

export default TargetSummaryDetails;
